"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnavOverviewSection = void 0;
const ui_1 = require("@app/component/ui");
const dropdown_input_1 = require("@app/component/ui/forms/dropdown-input/dropdown-input");
const form_grid_layout_1 = require("@app/component/ui/forms/form-grid-layout");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const maybe_1 = require("@app/util/functional/maybe");
const use_workspace_unavailability_categories_1 = require("@app/util/hooks/use-workspace-unavailability-categories");
const useMe_1 = require("@app/util/hooks/useMe");
const useSortedContracts_1 = require("@app/util/hooks/useSortedContracts");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const use_modal_1 = require("@app/util/modal/use-modal");
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const unavailability_context_1 = require("../unavailability-context");
const use_find_active_contract_1 = require("../util/use-find-active-contract");
const use_unav_date_controls_1 = require("../util/use-unav-date-controls");
const use_unavailability_categories_1 = require("../util/use-unavailability-categories");
const unav_attachments_section_1 = require("./unav-attachments-section");
const unav_note_section_1 = require("./unav-note-section");
const unav_period_section_1 = require("./unav-period-section");
const unav_repeat_section_1 = require("./unav-repeat-section");
const unav_work_minutes_inclusion_section_1 = require("./unav-work-minutes-inclusion-section");
const unavailability_infobox_1 = require("./unavailability-infobox");
const employeeHasId = (t) => 'id' in t && (0, util_1.isValue)(t.id);
const UnavOverviewSection = () => {
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const { formControl, isEmployeeCalendar, timePeriodInput, setTimePeriodInput, setValue, isEditing, canEdit, initialData, } = (0, unavailability_context_1.useUnavailability)();
    const { closeModal } = (0, use_modal_1.useModal)();
    const me = (0, useMe_1.useMe)();
    const userIsMe = initialData?.userId === me.id;
    const meContracts = me.roles?.flatMap((role) => role.contracts).filter(util_1.isValue);
    const requestPendingAction = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: 'requestPendingAction',
    }) ?? initialData?.requestPendingAction;
    const isRequest = (0, util_1.isValue)(requestPendingAction);
    const employees = (0, store_1.useRootSelector)((state) => Object.values(state.employees)
        .filter((emp) => !emp.external)
        .filter(employeeHasId));
    const unavailabilityCategoryOptions = (0, use_unavailability_categories_1.useUnavailabilityCategoryOptions)(isEmployeeCalendar, isRequest).filter((opt) => !('archived' in opt) || ('archived' in opt && !opt.archived));
    const contractTypes = new maybe_1.Maybe(workspace?.contractTypes?.filter(util_1.isValue)).get(() => []);
    const employeeOptions = react_1.default.useMemo(() => employees.map((emp) => ({
        label: `${emp.name ?? ''}${(0, util_1.isValue)(emp.employeeId) ? ` (${emp.employeeId})` : ''}`,
        value: emp.id,
    })), [employees]);
    const selectedEmployeeId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'user' });
    const selectedEmployee = employees.find((emp) => emp.id === selectedEmployeeId);
    const findActiveContract = (0, use_find_active_contract_1.useFindActiveContract)();
    const periodStart = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'period.start' });
    const contractOptions = react_1.default.useMemo(() => new maybe_1.Maybe(userIsMe ? meContracts : selectedEmployee?.contracts)
        .map((_contracts) => (0, useSortedContracts_1.parseContracts)(_contracts, periodStart).activeContracts)
        .map((_contracts) => _contracts
        // attach name from contract type
        .map((c) => ({
        ...c,
        name: contractTypes.find((ct) => ct.id === c.type)?.name,
    }))
        .map((c) => ({
        value: c.id ?? '',
        label: `${c.name ?? c.type ?? '-'}${(0, util_1.isValue)(c.contractId) ? ` (${c.contractId})` : ''}`,
    })))
        .get(() => []), [periodStart, selectedEmployee?.contracts, meContracts, contractTypes]);
    const contractController = (0, react_hook_form_1.useController)({
        control: formControl,
        name: 'contractId',
    });
    const { periodStartController, periodEndController, handleDateChange } = (0, use_unav_date_controls_1.useUnavDateControls)({
        formControl,
        timePeriodInput,
        setTimePeriodInput,
        setValue,
    });
    const _categoryId = (0, react_hook_form_1.useController)({ control: formControl, name: 'categoryId' });
    react_1.default.useEffect(() => {
        if (_categoryId.field.value === 'newCategory') {
            util_1.routeUtil.navigate('/workspace/timeoff-categories');
            closeModal();
        }
    }, [_categoryId.field.value, closeModal]);
    const shouldHideEmployeeSelect = isEmployeeCalendar;
    const canManagerCalendarEdit = !isEmployeeCalendar && canEdit;
    const canEditEmployee = initialData?.categoryId === use_workspace_unavailability_categories_1.AVAILABILITY_VALUE ||
        initialData?.categoryId === use_workspace_unavailability_categories_1.UNAVAILABILITY_VALUE;
    const canEditFields = isEmployeeCalendar ? !isEditing : canEdit;
    const employeeEditableFields = isEmployeeCalendar
        ? canEditEmployee
            ? true
            : !isEditing
        : canEdit;
    return (react_1.default.createElement("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            paddingBottom: '2.25rem',
        } },
        react_1.default.createElement(form_grid_layout_1.FormGridLayout, null,
            shouldHideEmployeeSelect ? null : (react_1.default.createElement(dropdown_input_1.DropdownInput, { testid: 'timeoff-employee-dropdown', label: (0, util_1.t)('EMPLOYEE'), options: employeeOptions, searchableOptions: true, control: formControl, name: 'user', transformer: {
                    in: (v) => employeeOptions.find((emp) => emp.value === v) ??
                        dropdown_input_1.DropdownInputEmptyOption,
                    out: (v) => {
                        const userActiveContract = findActiveContract(v.value, periodStart);
                        contractController.field.onChange(userActiveContract?.id);
                        return v.value;
                    },
                }, rules: {
                    required: {
                        value: true,
                        message: (0, util_1.t)('FORM_ERR_MISSING_REQUIRED'),
                    },
                }, disabled: !canManagerCalendarEdit })),
            react_1.default.createElement(dropdown_input_1.DropdownInput, { options: unavailabilityCategoryOptions, label: (0, util_1.t)('UNAV_TYPE'), control: formControl, name: 'categoryId', sortedOptions: false, searchableOptions: true, rules: {
                    required: {
                        value: true,
                        message: (0, util_1.t)('FORM_ERR_MISSING_REQUIRED'),
                    },
                }, disabled: !employeeEditableFields }),
            react_1.default.createElement(ui_1.PeriodSelect, { label: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('DATE')), value: {
                    start: periodStartController.field.value ?? null,
                    end: periodEndController.field.value ?? null,
                }, onChange: handleDateChange, calendar: undefined, singleSelector: true, displayType: ui_1.PeriodSelect.DISPLAY_TYPE.DATE, hideButtons: true, disabled: !canEditFields }),
            react_1.default.createElement(dropdown_input_1.DropdownInput, { options: contractOptions, label: (0, util_1.t)('AGREEMENT'), control: formControl, name: 'contractId', disabled: !employeeEditableFields })),
        react_1.default.createElement(unavailability_infobox_1.UnavailabilityInfobox, null),
        react_1.default.createElement(unav_period_section_1.UnavPeriodSection, null),
        react_1.default.createElement(unav_work_minutes_inclusion_section_1.UnavWorkMinutesInclusionSection, null),
        react_1.default.createElement(unav_note_section_1.UnavNoteSection, null),
        react_1.default.createElement(unav_attachments_section_1.UnavAttachmentsSection, null),
        react_1.default.createElement(unav_repeat_section_1.UnavRepeatSection, null)));
};
exports.UnavOverviewSection = UnavOverviewSection;
